/**
 * Map Editor State Management
 * Provides editor context: mode, selection, undo/redo, save state
 */

import { createContext, useContext, useReducer, useCallback, useRef, type ReactNode } from 'react';
import type { PhotoData } from '../../../shared/types/project';
import type { FloorPlanOverlay } from '../../../shared/types/floorplan';
import type { PhotoCategory } from '../../../shared/types/project';

// ─── Types ─────────────────────────────────────────────────────────────────

export type EditorMode = 'select' | 'move' | 'place-photo' | 'place-overlay';
export type SaveStatus = 'saved' | 'saving' | 'unsaved' | 'error';

export interface EditorAction {
  type: string;
  description: string;
  undo: () => void;
  redo: () => void;
}

interface EditorState {
  mode: EditorMode;
  selectedIds: string[];
  selectedType: 'photo' | 'overlay' | null;
  photos: PhotoData[];
  floorPlans: FloorPlanOverlay[];
  customCategories: PhotoCategory[];
  categoryFilters: Set<string>;
  isDirty: boolean;
  saveStatus: SaveStatus;
  pendingPhotoData: Partial<PhotoData> | null;
  aligningOverlayId: string | null;
  cursorPosition: { lng: number; lat: number } | null;
  orthoVisible: boolean;
  dsmVisible: boolean;
}

type Action =
  | { type: 'SET_MODE'; mode: EditorMode }
  | { type: 'SET_SELECTION'; ids: string[]; itemType: 'photo' | 'overlay' | null }
  | { type: 'ADD_TO_SELECTION'; id: string }
  | { type: 'CLEAR_SELECTION' }
  | { type: 'SET_PHOTOS'; photos: PhotoData[] }
  | { type: 'UPDATE_PHOTO'; id: string; updates: Partial<PhotoData> }
  | { type: 'ADD_PHOTO'; photo: PhotoData }
  | { type: 'DELETE_PHOTOS'; ids: string[] }
  | { type: 'SET_FLOOR_PLANS'; floorPlans: FloorPlanOverlay[] }
  | { type: 'UPDATE_FLOOR_PLAN'; id: string; updates: Partial<FloorPlanOverlay> }
  | { type: 'ADD_FLOOR_PLAN'; floorPlan: FloorPlanOverlay }
  | { type: 'DELETE_FLOOR_PLAN'; id: string }
  | { type: 'SET_CUSTOM_CATEGORIES'; categories: PhotoCategory[] }
  | { type: 'TOGGLE_CATEGORY_FILTER'; categoryId: string }
  | { type: 'SET_DIRTY'; dirty: boolean }
  | { type: 'SET_SAVE_STATUS'; status: SaveStatus }
  | { type: 'SET_PENDING_PHOTO'; data: Partial<PhotoData> | null }
  | { type: 'SET_ALIGNING_OVERLAY'; id: string | null }
  | { type: 'SET_CURSOR'; position: { lng: number; lat: number } | null }
  | { type: 'BATCH_UPDATE_PHOTOS'; ids: string[]; updates: Partial<PhotoData> }
  | { type: 'TOGGLE_ORTHO' }
  | { type: 'TOGGLE_DSM' };

function reducer(state: EditorState, action: Action): EditorState {
  switch (action.type) {
    case 'SET_MODE':
      return { ...state, mode: action.mode, pendingPhotoData: null, aligningOverlayId: null };
    case 'SET_SELECTION':
      return { ...state, selectedIds: action.ids, selectedType: action.itemType };
    case 'ADD_TO_SELECTION':
      return { ...state, selectedIds: state.selectedIds.includes(action.id) ? state.selectedIds.filter(id => id !== action.id) : [...state.selectedIds, action.id] };
    case 'CLEAR_SELECTION':
      return { ...state, selectedIds: [], selectedType: null };
    case 'SET_PHOTOS':
      return { ...state, photos: action.photos };
    case 'UPDATE_PHOTO':
      return { ...state, photos: state.photos.map(p => p.id === action.id ? { ...p, ...action.updates } : p), isDirty: true };
    case 'ADD_PHOTO':
      return { ...state, photos: [...state.photos, action.photo], isDirty: true };
    case 'DELETE_PHOTOS':
      return { ...state, photos: state.photos.filter(p => !action.ids.includes(p.id)), selectedIds: state.selectedIds.filter(id => !action.ids.includes(id)), isDirty: true };
    case 'SET_FLOOR_PLANS':
      return { ...state, floorPlans: action.floorPlans };
    case 'UPDATE_FLOOR_PLAN':
      return { ...state, floorPlans: state.floorPlans.map(fp => fp.id === action.id ? { ...fp, ...action.updates } : fp), isDirty: true };
    case 'ADD_FLOOR_PLAN':
      return { ...state, floorPlans: [...state.floorPlans, action.floorPlan], isDirty: true };
    case 'DELETE_FLOOR_PLAN':
      return { ...state, floorPlans: state.floorPlans.filter(fp => fp.id !== action.id), isDirty: true };
    case 'SET_CUSTOM_CATEGORIES':
      return { ...state, customCategories: action.categories };
    case 'TOGGLE_CATEGORY_FILTER': {
      const filters = new Set(state.categoryFilters);
      if (filters.has(action.categoryId)) filters.delete(action.categoryId);
      else filters.add(action.categoryId);
      return { ...state, categoryFilters: filters };
    }
    case 'SET_DIRTY':
      return { ...state, isDirty: action.dirty };
    case 'SET_SAVE_STATUS':
      return { ...state, saveStatus: action.status };
    case 'SET_PENDING_PHOTO':
      return { ...state, pendingPhotoData: action.data };
    case 'SET_ALIGNING_OVERLAY':
      return { ...state, aligningOverlayId: action.id };
    case 'SET_CURSOR':
      return { ...state, cursorPosition: action.position };
    case 'BATCH_UPDATE_PHOTOS':
      return { ...state, photos: state.photos.map(p => action.ids.includes(p.id) ? { ...p, ...action.updates } : p), isDirty: true };
    case 'TOGGLE_ORTHO':
      return { ...state, orthoVisible: !state.orthoVisible };
    case 'TOGGLE_DSM':
      return { ...state, dsmVisible: !state.dsmVisible };
    default:
      return state;
  }
}

// ─── Context ───────────────────────────────────────────────────────────────

interface EditorContextValue {
  state: EditorState;
  dispatch: React.Dispatch<Action>;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  pushAction: (action: EditorAction) => void;
}

const EditorContext = createContext<EditorContextValue | null>(null);

export function useMapEditor() {
  const ctx = useContext(EditorContext);
  if (!ctx) throw new Error('useMapEditor must be used within MapEditorProvider');
  return ctx;
}

// ─── Provider ──────────────────────────────────────────────────────────────

const INITIAL_STATE: EditorState = {
  mode: 'select',
  selectedIds: [],
  selectedType: null,
  photos: [],
  floorPlans: [],
  customCategories: [],
  categoryFilters: new Set(),
  isDirty: false,
  saveStatus: 'saved',
  pendingPhotoData: null,
  aligningOverlayId: null,
  cursorPosition: null,
  orthoVisible: true,
  dsmVisible: true,
};

const MAX_UNDO = 50;

export function MapEditorProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const undoStackRef = useRef<EditorAction[]>([]);
  const redoStackRef = useRef<EditorAction[]>([]);
  const [, forceUpdate] = useReducer(x => x + 1, 0);

  const pushAction = useCallback((action: EditorAction) => {
    undoStackRef.current.push(action);
    if (undoStackRef.current.length > MAX_UNDO) undoStackRef.current.shift();
    redoStackRef.current = [];
    forceUpdate();
  }, []);

  const undo = useCallback(() => {
    const action = undoStackRef.current.pop();
    if (action) {
      action.undo();
      redoStackRef.current.push(action);
      forceUpdate();
    }
  }, []);

  const redo = useCallback(() => {
    const action = redoStackRef.current.pop();
    if (action) {
      action.redo();
      undoStackRef.current.push(action);
      forceUpdate();
    }
  }, []);

  return (
    <EditorContext.Provider value={{
      state,
      dispatch,
      undo,
      redo,
      canUndo: undoStackRef.current.length > 0,
      canRedo: redoStackRef.current.length > 0,
      pushAction,
    }}>
      {children}
    </EditorContext.Provider>
  );
}
